// Migrazione da axios a $fetch per risolvere vulnerabilità di sicurezza
// Mantiene compatibilità completa con l'interfaccia axios esistente
import { useAuthStore } from '@/stores/auth';
import routeFactory from '@/lib/nuxtGlobalRoute';
import authService from '@/lib/services/authService';

/**
 * Interfaccia per emulare AxiosRequestConfig con $fetch
 */
interface FetchRequestConfig {
	method?: string;
	baseURL?: string;
	url?: string;
	headers?: Record<string, string>;
	body?: any;
	responseType?: 'json' | 'text' | 'blob';
	_retry?: boolean;
}

/**
 * Interfaccia per emulare AxiosResponse con $fetch
 */
interface FetchResponse<T = any> {
	data: T;
	status: number;
	statusText: string;
	headers: Record<string, string>;
	config: FetchRequestConfig;
}

/**
 * Interfaccia per emulare AxiosError con $fetch
 */
interface FetchError extends Error {
	response?: {
		status: number;
		statusText: string;
		data?: any;
	};
	config?: FetchRequestConfig;
}

/**
 * Configurazione di base per l'API
 */
const apiConfig = {
	baseURL: process.env.BASE_URL || 'http://localhost:3001/api/mock/',
	headers: {
		'Content-Type': 'application/json',
		'x-source': 'BackOffice',
	},
};

/**
 * Aggiunge il token di autorizzazione agli headers se disponibile
 */
function _getAuthHeaders(): Record<string, string> {
	const authStore = useAuthStore();
	const token = authStore.accessToken;
	const headers: Record<string, string> = { ...apiConfig.headers };

	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	return headers;
}

/**
 * Gestisce la risposta di errore, in particolare per errori 401
 */
function _handleErrorResponse(error: FetchError): void {
	if (error.response?.status === 401) {
		const route = routeFactory();
		const shouldRedirect = route.name !== 'login___it';
		authService.logout(shouldRedirect);
	}
}

/**
 * Converte errore $fetch in formato compatibile con axios
 */
function _createFetchError(error: any, config: FetchRequestConfig): FetchError {
	const fetchError = new Error(error.message || 'Request failed') as FetchError;
	fetchError.config = config;

	if (error.response) {
		fetchError.response = {
			status: error.response.status || 500,
			statusText: error.response.statusText || 'Internal Server Error',
			data: error.response._data || error.data,
		};
	}

	return fetchError;
}

/**
 * Wrapper per $fetch che emula il comportamento di axios
 */
async function _fetchWrapper<T = any>(
	url: string,
	options: {
		method?: string;
		body?: any;
		headers?: Record<string, string>;
		responseType?: 'json' | 'text' | 'blob';
		_retry?: boolean;
	} = {},
): Promise<FetchResponse<T>> {
	const config: FetchRequestConfig = {
		method: options.method || 'GET',
		baseURL: apiConfig.baseURL,
		url,
		headers: { ..._getAuthHeaders(), ...options.headers },
		body: options.body,
		responseType: options.responseType || 'json',
		_retry: options._retry,
	};

	const fullUrl = url.startsWith('http') ? url : `${config.baseURL?.replace(/\/$/, '')}${url}`;

	try {
		// Usa l'API fetch nativa per le chiamate HTTP
		const fetchOptions: RequestInit = {
			method: config.method,
			headers: config.headers,
		};

		if (config.body && config.method !== 'GET') {
			// Gestione corretta di URLSearchParams per form-urlencoded
			if (config.body instanceof URLSearchParams) {
				fetchOptions.body = config.body.toString();
			}
			// Gestione stringhe (già pronte)
			else if (typeof config.body === 'string') {
				fetchOptions.body = config.body;
			}
			// Gestione oggetti (JSON)
			else {
				fetchOptions.body = JSON.stringify(config.body);
			}
		}

		const response = await fetch(fullUrl, fetchOptions);

		let data: T;
		const contentType = response.headers.get('content-type');

		// Gestione response basata su responseType o content-type
		if (config.responseType === 'blob') {
			data = (await response.blob()) as any;
		} else if (
			config.responseType === 'text' ||
			(!config.responseType && contentType && !contentType.includes('application/json'))
		) {
			data = (await response.text()) as any;
		} else {
			// Default: JSON - gestisce risposte vuote per operazioni void
			try {
				const text = await response.text();
				if (text.trim() === '') {
					data = undefined as any; // Risposta vuota (204 No Content o 200 OK vuoto)
				} else {
					data = JSON.parse(text);
				}
			} catch (parseError) {
				console.error('❌ ERROR api.text:', parseError);
				// Fallback per contenuti non-JSON
				data = undefined as any;
			}
		}

		if (!response.ok) {
			const error = new Error(`HTTP Error ${response.status}`);
			(error as any).response = {
				status: response.status,
				statusText: response.statusText,
				_data: data,
			};
			throw error;
		}

		// Conversione headers per compatibilità
		const headers: Record<string, string> = {};
		response.headers.forEach((value, key) => {
			headers[key] = value;
		});

		return {
			data,
			status: response.status,
			statusText: response.statusText,
			headers,
			config,
		};
	} catch (error: any) {
		const fetchError = _createFetchError(error, config);

		// Gestione retry per 401 (come negli interceptors axios originali)
		if (
			fetchError.response?.status === 401 &&
			!config._retry &&
			!url.includes('Authorization/RefreshToken')
		) {
			config._retry = true;
			try {
				const refreshTokenResult = await authService.refreshToken();
				if (refreshTokenResult) {
					// Retry con nuovo token
					return _fetchWrapper<T>(url, { ...options, _retry: true });
				} else {
					_handleErrorResponse(fetchError);
					throw fetchError;
				}
			} catch {
				_handleErrorResponse(fetchError);
				throw fetchError;
			}
		} else {
			_handleErrorResponse(fetchError);
			throw fetchError;
		}
	}
}

/**
 * API client che emula l'interfaccia axios per compatibilità completa
 */
const api = {
	get<T = any>(
		url: string,
		config?: {
			headers?: Record<string, string>;
			responseType?: 'json' | 'text' | 'blob';
			params?: Record<string, any>;
		},
	): Promise<FetchResponse<T>> {
		let finalUrl = url;

		// Converti params in query string per compatibilità axios
		if (config?.params) {
			const searchParams = new URLSearchParams();
			Object.entries(config.params).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					// Gestisce array (per costCenterIds e simili)
					if (Array.isArray(value)) {
						value.forEach((item) => searchParams.append(key, String(item)));
					}
					// Gestisce Date objects
					else if (value instanceof Date) {
						searchParams.append(key, value.toISOString());
					}
					// Gestisce valori primitivi
					else {
						searchParams.append(key, String(value));
					}
				}
			});

			const queryString = searchParams.toString();
			if (queryString) {
				finalUrl += (url.includes('?') ? '&' : '?') + queryString;
			}
		}

		return _fetchWrapper<T>(finalUrl, {
			method: 'GET',
			headers: config?.headers,
			responseType: config?.responseType,
		});
	},

	post<T = any>(
		url: string,
		data?: any,
		config?: {
			headers?: Record<string, string>;
			responseType?: 'json' | 'text' | 'blob';
			params?: Record<string, any>;
		},
	): Promise<FetchResponse<T>> {
		let finalUrl = url;

		// Converti params in query string per compatibilità axios
		if (config?.params) {
			const searchParams = new URLSearchParams();
			Object.entries(config.params).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					if (Array.isArray(value)) {
						value.forEach((item) => searchParams.append(key, String(item)));
					} else if (value instanceof Date) {
						searchParams.append(key, value.toISOString());
					} else {
						searchParams.append(key, String(value));
					}
				}
			});

			const queryString = searchParams.toString();
			if (queryString) {
				finalUrl += (url.includes('?') ? '&' : '?') + queryString;
			}
		}

		return _fetchWrapper<T>(finalUrl, {
			method: 'POST',
			body: data,
			headers: config?.headers,
			responseType: config?.responseType,
		});
	},

	put<T = any>(
		url: string,
		data?: any,
		config?: {
			headers?: Record<string, string>;
			responseType?: 'json' | 'text' | 'blob';
			params?: Record<string, any>;
		},
	): Promise<FetchResponse<T>> {
		let finalUrl = url;

		// Converti params in query string per compatibilità axios
		if (config?.params) {
			const searchParams = new URLSearchParams();
			Object.entries(config.params).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					if (Array.isArray(value)) {
						value.forEach((item) => searchParams.append(key, String(item)));
					} else if (value instanceof Date) {
						searchParams.append(key, value.toISOString());
					} else {
						searchParams.append(key, String(value));
					}
				}
			});

			const queryString = searchParams.toString();
			if (queryString) {
				finalUrl += (url.includes('?') ? '&' : '?') + queryString;
			}
		}

		return _fetchWrapper<T>(finalUrl, {
			method: 'PUT',
			body: data,
			headers: config?.headers,
			responseType: config?.responseType,
		});
	},

	patch<T = any>(
		url: string,
		data?: any,
		config?: { headers?: Record<string, string>; responseType?: 'json' | 'text' | 'blob' },
	): Promise<FetchResponse<T>> {
		return _fetchWrapper<T>(url, {
			method: 'PATCH',
			body: data,
			headers: config?.headers,
			responseType: config?.responseType,
		});
	},

	delete<T = any>(
		url: string,
		config?: { headers?: Record<string, string>; responseType?: 'json' | 'text' | 'blob' },
	): Promise<FetchResponse<T>> {
		return _fetchWrapper<T>(url, {
			method: 'DELETE',
			headers: config?.headers,
			responseType: config?.responseType,
		});
	},
};

export default api;
export type { FetchResponse, FetchError, FetchRequestConfig };
